import { openIdFromAccountId, type Account } from '../../../entities/account';
import { getMediaInsights, getRecentMedia } from '../../../shared/api/reports.api';
import { getTikTokVideosInRange } from '../../../shared/api/tiktok.api';
import { mapWithConcurrency } from '../../../shared/lib/mapWithConcurrency';
import { fetchCollectedStats } from '../api/account-report';

import { statRowFromMedia, type AccountStatRow } from './stat-row';

/**
 * Сбор статистики публикаций одного аккаунта за период.
 *
 * Вынесено из AccountsStatsPage, когда отчёту «Топ видео» понадобилось пройти
 * тот же сбор по нескольким аккаунтам подряд. Держать копию нельзя: оба места
 * обязаны давать одинаковые цифры, а сбор здесь — это как раз место, где они
 * расходятся (фолбэк сервер → браузер, учёт неотвеченных публикаций).
 */

export type CollectRange = { from: Date; to: Date };

export type CollectOutcome = {
  rows: AccountStatRow[];
  /** Публикации, по которым Instagram не ответил метриками: легли нулями. */
  noInsights: number;
  /** Публикации, сбор по которым упал: в rows их нет. */
  threw: number;
  total: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;
// Публикации обрабатываются параллельно: на сотне последовательный обход —
// это триста запросов к Instagram один за другим.
const STAT_CONCURRENCY = 6;

/** TikTok: id вида "tt:<openId>". Период включает весь последний день. */
export async function collectTikTokStats(
  accountId: string,
  range: CollectRange,
): Promise<CollectOutcome> {
  const openId = openIdFromAccountId(accountId);
  const vids = await getTikTokVideosInRange(
    openId,
    range.from.getTime(),
    range.to.getTime() + DAY_MS - 1,
  );

  const rows: AccountStatRow[] = vids
    .slice()
    .sort((a, b) => a.createTime - b.createTime)
    .map((v) => {
      const engagement = v.likeCount + v.commentCount + v.shareCount;
      return {
        mediaId: v.id,
        thumbnailUrl: v.coverImageUrl,
        caption: '',
        permalink: v.shareUrl,
        timestamp: new Date(v.createTime * 1000).toISOString(),
        mediaType: 'VIDEO',
        views: v.viewCount,
        likes: v.likeCount,
        comments: v.commentCount,
        shares: v.shareCount,
        // Заглушка: в Display API сохранений нет вовсе (см. top-videos.ts).
        saved: 0,
        reach: 0,
        crossposted_views: 0,
        facebook_views: 0,
        er: v.viewCount > 0 ? (engagement / v.viewCount) * 100 : 0,
      };
    });

  return { rows, noInsights: 0, threw: 0, total: rows.length };
}

/**
 * Instagram: сбор двумя путями с одинаковыми цифрами (общая statRowFromMedia).
 * Основной — серверный /api/reports/collect (те же запросы к Graph API, но с
 * бэкенда и параллельнее), запасной — обход из браузера, если бэкенд
 * недоступен или ещё не обновлён.
 */
export async function collectInstagramStats(
  account: Account,
  range: CollectRange,
  onProgress: (message: string) => void = () => {},
): Promise<CollectOutcome> {
  const viaServer = async (): Promise<CollectOutcome> => {
    const byIndex: (AccountStatRow | undefined)[] = [];
    let noInsights = 0;
    let threw = 0;
    await fetchCollectedStats(
      { accountId: account.id, from: range.from, to: range.to },
      {
        onListed: (total) => onProgress(`Обработка ${total} публикаций...`),
        onRow: (r) => {
          if (r.failed) {
            threw += 1;
            console.error(`[Account Stats] ${r.media.permalink} — сбой сбора метрик`);
            return;
          }
          if (r.insightsMissing) {
            noInsights += 1;
            console.warn(`[Account Stats] ${r.media.permalink} — метрики не пришли`);
          }
          byIndex[r.index] = statRowFromMedia(r.media, r.insights);
        },
        onProgress: (done, total) => onProgress(`Обработка публикаций ${done}/${total}...`),
      },
    );
    const rows = byIndex.filter((r): r is AccountStatRow => Boolean(r));
    return { rows, noInsights, threw, total: rows.length + threw };
  };

  const viaBrowser = async (): Promise<CollectOutcome> => {
    // У телефонных аккаунтов токена нет: без этой проверки запросы уходили в
    // Graph API с undefined и падали невнятной ошибкой где-то внутри.
    const { accessToken, instagramUserId } = account;
    if (!accessToken || !instagramUserId) {
      throw new Error('у аккаунта нет токена Instagram, собрать статистику нельзя');
    }

    const allMedia = await getRecentMedia(accessToken, instagramUserId, 5000, {
      from: range.from,
      to: range.to,
    });
    onProgress(`Обработка ${allMedia.length} публикаций...`);

    // Старые → новые: отчёт начинается с ранней даты.
    allMedia.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    const settled = await mapWithConcurrency(
      allMedia,
      STAT_CONCURRENCY,
      async (media) => {
        const insights = await getMediaInsights(
          accessToken,
          media.id,
          media.media_type,
          media.media_product_type,
        );
        // getMediaInsights на любой ошибке отдаёт пустой объект, а не бросает.
        // Без этой проверки неотвеченная публикация молча оседала бы нулями.
        const insightsMissing = Object.keys(insights).length === 0;
        return { insightsMissing, row: statRowFromMedia(media, insights) };
      },
      (done, total) => onProgress(`Обработка публикаций ${done}/${total}...`),
    );

    const rows = settled.flatMap((r) => (r.ok ? [r.value.row] : []));
    const noInsights = settled.filter((r) => r.ok && r.value.insightsMissing).length;
    settled.forEach((r, i) => {
      if (!r.ok) console.error(`[Account Stats] ${allMedia[i].permalink} — сбой:`, r.error);
      else if (r.value.insightsMissing) console.warn(`[Account Stats] ${allMedia[i].permalink} — метрики не пришли`);
    });
    return { rows, noInsights, threw: settled.length - rows.length, total: settled.length };
  };

  try {
    return await viaServer();
  } catch (err) {
    console.warn('[Account Stats] серверный сбор недоступен, откат на браузерный:', err);
    onProgress('Загрузка публикаций...');
    return viaBrowser();
  }
}
